import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { User, Alert, Facility } from '../types';
import { getAlerts, acknowledgeAlert, resolveAlert } from '../services/api';

interface AlertsProps {
  user: User;
  onLogout: () => void;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

const Alerts: React.FC<AlertsProps> = ({ user, onLogout, facilities, selectedFacility, onFacilityChange }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'all'>('active');
  const [severityFilter, setSeverityFilter] = useState<string>('all');

  useEffect(() => {
    loadAlerts();
  }, [filter, selectedFacility]);

  const loadAlerts = async () => {
    try {
      const data = await getAlerts(filter);

      // Filter by selected facility if applicable
      const selectedFacilityName = facilities?.find(f => f.id === selectedFacility)?.name;
      const filteredData = selectedFacilityName
        ? data.filter((a: Alert) => a.facility_name === selectedFacilityName)
        : data;

      setAlerts(filteredData);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await acknowledgeAlert(id);
      await loadAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await resolveAlert(id);
      await loadAlerts();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  const filteredAlerts = severityFilter === 'all'
    ? alerts
    : alerts.filter(a => a.severity === severityFilter);

  const severityCounts = {
    critical: alerts.filter(a => a.severity === 'critical').length,
    high: alerts.filter(a => a.severity === 'high').length,
    medium: alerts.filter(a => a.severity === 'medium').length,
    low: alerts.filter(a => a.severity === 'low').length,
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-500 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-500 text-orange-800';
      case 'medium': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'low': return 'bg-blue-100 border-blue-500 text-blue-800';
      default: return 'bg-gray-100 border-gray-500 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
        <div className="text-center">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
      <div className="px-4 sm:px-0">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Alerts</h1>

        {/* Severity Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {Object.entries(severityCounts).map(([severity, count]) => (
            <div
              key={severity}
              className={`p-4 rounded-lg cursor-pointer ${
                severityFilter === severity ? 'ring-2 ring-primary-500' : ''
              } ${getSeverityColor(severity)}`}
              onClick={() => setSeverityFilter(severityFilter === severity ? 'all' : severity)}
            >
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-sm font-medium capitalize">{severity}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-md ${
              filter === 'active'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md ${
              filter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            All
          </button>
          {severityFilter !== 'all' && (
            <button
              onClick={() => setSeverityFilter('all')}
              className="px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300"
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Alerts List */}
        <div className="space-y-4">
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-500">No alerts found</p>
            </div>
          ) : (
            filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border-l-4 shadow ${getSeverityColor(alert.severity)}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert.status === 'active' ? 'bg-red-100 text-red-800' :
                        alert.status === 'acknowledged' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {alert.status}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium">{alert.title}</h3>
                    <p className="text-sm mt-1">{alert.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      <span>
                        Patient: <Link
                          to={`/patients/${alert.patient_id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          {alert.patient_name}
                        </Link>
                      </span>
                      <span>Room: {alert.room_number}</span>
                      <span>Facility: {alert.facility_name}</span>
                      <span>{new Date(alert.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-3">
                      <Link
                        to={`/patients/${alert.patient_id}`}
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        📊 View Patient Charts & History
                        <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 ml-4">
                    {alert.status === 'active' && (
                      <button
                        onClick={() => handleAcknowledge(alert.id)}
                        className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                      >
                        Acknowledge
                      </button>
                    )}
                    {(alert.status === 'active' || alert.status === 'acknowledged') && (
                      <button
                        onClick={() => handleResolve(alert.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Resolve
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Alerts;
